import { useMemo, useState } from 'react';

import { groupPresences } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/helpers/batchSelectionHelpers';
import {
  GROUP_OPTION_VALUES,
  THUMBNAIL_SIZE_MIN,
} from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/types';
import { sortGroupPresences } from '@/features/broadcast-schedule-program/helpers/sort-by-key';

export function useAddPresencesGrouping<T extends { id?: string; tempId?: string }>(
  items: T[],
  entityIdKey: keyof T
) {
  const [thumbnailSize, setThumbnailSize] = useState<number>(THUMBNAIL_SIZE_MIN);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const groupedItems = useMemo(() => {
    const grouped = groupPresences(items as any, GROUP_OPTION_VALUES.Presence, entityIdKey as any);
    return sortGroupPresences(grouped as any, undefined, 'asc');
  }, [items, entityIdKey]);

  const toggleGroupSelection = (ids: string[]) => {
    const allSelected = ids.length > 0 && ids.every(id => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds(prev => prev.filter(id => !ids.includes(id)));
    } else {
      setSelectedIds(prev => Array.from(new Set([...prev, ...ids])));
    }
  };

  const toggleItemSelection = (id: string) => {
    setSelectedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };

  const isGroupFullySelected = (ids: string[]): boolean =>
    ids.length > 0 && ids.every(id => selectedIds.includes(id));

  const isGroupPartiallySelected = (ids: string[]): boolean =>
    ids.some(id => selectedIds.includes(id)) && !isGroupFullySelected(ids);

  return {
    groupedItems,
    selectedIds,
    setSelectedIds,
    thumbnailSize,
    setThumbnailSize,
    toggleGroupSelection,
    toggleItemSelection,
    isGroupFullySelected,
    isGroupPartiallySelected,
  };
}
